
import SaplingInputHighlights from './SaplingInputHighlights.vue';
import SaplingListPreview from './SaplingListPreview.vue';
import SaplingListNumbering from './SaplingListNumbering.vue';
import PreviousGenes from './PreviousGenes.vue';

import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { GeneticsMap } from '@/services/crossbreeding-service/models';
import { playAudio } from '@/lib/ui-utils';
import StoredSet from '@/interfaces/stored-set';
import eventBus, { GLOBAL_EVENT_SELECTED_PLANT_TYPE_CHANGED } from '@/lib/global-event-bus';

@Component({
  components: {
    SaplingInputHighlights,
    SaplingListPreview,
    SaplingListNumbering,
    PreviousGenes
  }
})
export default class GeneInputs extends Vue {
  @Prop({ type: Boolean }) readonly disabled: boolean;
  /** Scanning uses readonly (not disabled) so Vuetify still applies programmatic gene lines from the scanner. */
  @Prop({ type: Boolean, default: false }) readonly readOnlyWhileScanning: boolean;
  @Prop({ type: Boolean }) readonly soundsEnabled: boolean;
  @Prop({ type: Boolean }) readonly autoSaveInputSets: boolean;
  @Prop({ type: String }) readonly selectedPlantTypeName: string;
  @Prop({ type: GeneticsMap, required: false }) readonly highlightedMap: GeneticsMap | null;

  // fix for Safari not respecting new line in placeholder
  placeholder = `YGXWHH\nXWHYYG\nGHGWYY\netc...`.replaceAll('\n', ' '.repeat(100));
  saplingGenes = ``;
  isFormValid = false;
  isInputFocused = false;
  animatePreviousGenesTabIn = false;
  wasLastInputLongTimeAgo = false;
  lastInputTimeStamp = null;
  lastInputLongAgoTimeoutRef: undefined | NodeJS.Timeout;
  tab = 0;
  shareCopied = false;
  private shareCopiedTimeout: NodeJS.Timeout | null = null;
  private urlSyncTimeout: NodeJS.Timeout | null = null;

  /** Keeps line numbers / preview / highlights aligned when the gene textarea scrolls. */
  geneTextareaScrollTop = 0;

  private textareaScrollListenerBound: HTMLTextAreaElement | null = null;

  private geneTextareaResizeObserver: ResizeObserver | null = null;

  private geneEditorResizeObserver: ResizeObserver | null = null;

  private windowResizeBound: (() => void) | null = null;

  /** Visible viewport for line numbers / preview (matches textarea box). */
  overlayClipTopPx = 52;

  overlayClipHeightPx = 200;

  sourceSaplingRules = [
    (v: string) =>
      /^([GHWYX]{6}\n{1})*([GHWYX]{6}\n{0})*\n*$/.test(v) ||
      'The list of genes is incomplete or invalid. Review if you provided them all correctly.',
    (v: string) => v !== '' || 'Give me some genes to work with!',
    (v: string) => !/^([GHWYX]{6}\n{0})*\n*$/.test(v) || 'Give me some more genes to work with!'
  ];

  get saplingGeneList() {
    return this.saplingGenes === '' ? [] : this.saplingGenes.trim().split(/\r?\n/);
  }

  get highlightInputErrors() {
    return this.wasLastInputLongTimeAgo || !this.isInputFocused;
  }

  /** Minimum rows for auto-grow (Vuetify uses rows × rowHeight as the height floor). */
  geneTextareaMinRows = 5;

  /** Match font-size 0.95rem × line-height 1.45 at default 16px root (~22px). */
  geneTextareaRowHeightPx = 22;

  get geneOverlayScrollStyle(): Record<string, string> {
    return { transform: `translateY(-${this.geneTextareaScrollTop}px)` };
  }

  mounted() {
    const searchParams = new URLSearchParams(window.location.search);
    const genesUrlParam = searchParams.get('genes');
    if (genesUrlParam) {
      this.saplingGenes = genesUrlParam.split(',').join('\n');
    }

    if (this.saplingGenes !== '') {
      this.checkFormValidity();
    }

    this.$nextTick(() => {
      this.bindGeneTextareaScrollListener();
      this.setupGeneEditorResizeObserver();
      this.setupWindowResizeListener();
      this.refreshGeneTextareaGeometry();
    });
  }

  updated() {
    this.$nextTick(() => {
      this.bindGeneTextareaScrollListener();
      this.syncOverlayClipGeometry();
    });
  }

  beforeDestroy() {
    this.unbindGeneTextareaScrollListener();
    this.teardownGeneTextareaResizeObserver();
    this.teardownGeneEditorResizeObserver();
    this.teardownWindowResizeListener();
    if (this.urlSyncTimeout) clearTimeout(this.urlSyncTimeout);
    if (this.shareCopiedTimeout) clearTimeout(this.shareCopiedTimeout);
  }

  @Watch('saplingGenes')
  onSaplingGenesChanged() {
    if (this.urlSyncTimeout) clearTimeout(this.urlSyncTimeout);
    this.urlSyncTimeout = setTimeout(() => this.syncGenesUrl(), 600);
  }

  private syncGenesUrl() {
    const url = new URL(window.location.href);
    const genes = this.getDeduplicatedSaplingGeneList();
    if (genes.length > 0 && genes.every((g) => /^[GHWYX]{6}$/.test(g))) {
      url.searchParams.set('genes', genes.join(','));
    } else {
      url.searchParams.delete('genes');
    }
    window.history.replaceState(null, '', url.toString());
  }

  showShareConfirmation() {
    this.shareCopied = true;
    if (this.shareCopiedTimeout) clearTimeout(this.shareCopiedTimeout);
    this.shareCopiedTimeout = setTimeout(() => {
      this.shareCopied = false;
    }, 2000);
  }

  private getGeneTextareaEl(): HTMLTextAreaElement | null {
    const field = this.$refs.saplingGenesInput as Vue | undefined;
    return field?.$el?.querySelector?.('textarea') ?? null;
  }

  /** Index after the last character on the last non-empty row (ignores trailing blank lines). */
  private getEndOfLastNonEmptyLineOffset(value: string): number {
    const norm = value.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    if (norm === '') {
      return 0;
    }
    const lines = norm.split('\n');
    let pos = 0;
    let lastContentEnd = 0;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (line.length > 0) {
        lastContentEnd = pos + line.length;
      }
      pos += line.length;
      if (i < lines.length - 1) {
        pos += 1;
      }
    }
    return lastContentEnd;
  }

  /**
   * Caret target: first structural problem (blank line with genes below, bad chars, >6 letters on one row),
   * else end of an incomplete last line, else end of the last line that has characters (not an empty tail row).
   */
  private getPreferredGeneCaretOffset(value: string): number {
    const v = value.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    if (v === '') {
      return 0;
    }

    const lines = v.split('\n');
    let pos = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const hasNonEmptyBelow = lines.slice(i + 1).some((ln) => ln.length > 0);

      if (line.length === 0 && hasNonEmptyBelow) {
        return pos;
      }

      if (line.length > 0 && !/^[GHWYX]+$/.test(line)) {
        return pos;
      }

      if (line.length > 6) {
        return pos;
      }

      if (line.length > 0 && line.length < 6) {
        return pos + line.length;
      }

      if (/^[GHWYX]{6}$/.test(line)) {
        pos += line.length;
        if (i < lines.length - 1) {
          pos += 1;
        }
        continue;
      }

      /* Trailing empty row(s) after valid genes — do not land the caret on that blank line */
      if (line.length === 0) {
        continue;
      }

      return pos;
    }

    return this.getEndOfLastNonEmptyLineOffset(v);
  }

  /** Enter may only start a new plant after a full six-letter line (no blank rows from Return). */
  private isEnterAllowedAt(value: string, caret: number): boolean {
    const norm = value.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const c = Math.max(0, Math.min(caret, norm.length));
    const lastNl = norm.lastIndexOf('\n', c - 1);
    const lineStart = lastNl + 1;
    const nextNl = norm.indexOf('\n', c);
    const lineEnd = nextNl === -1 ? norm.length : nextNl;
    const left = norm.slice(lineStart, c);
    if (!/^[GHWYX]{6}$/.test(left)) {
      return false;
    }
    if (c < lineEnd) {
      return false;
    }
    return true;
  }

  private moveGeneCaretToPreferred(ta?: HTMLTextAreaElement) {
    const el = ta ?? this.getGeneTextareaEl();
    if (!el || this.disabled || this.readOnlyWhileScanning) {
      return;
    }
    const off = this.getPreferredGeneCaretOffset(this.saplingGenes);
    const safe = Math.max(0, Math.min(off, el.value.length));
    el.setSelectionRange(safe, safe);
  }

  private onGeneTextareaScroll = (e: Event) => {
    this.geneTextareaScrollTop = (e.target as HTMLTextAreaElement).scrollTop;
  };

  /**
   * Capture phase: Enter must never insert a raw newline unless it’s after a full six-gene line
   * (bubble-phase preventDefault is too late with Vuetify’s textarea wrapper).
   */
  private onGeneTextareaKeydownCapture = (event: KeyboardEvent) => {
    if (event.key !== 'Enter' || event.ctrlKey || event.metaKey) {
      return;
    }
    const ta = event.target as HTMLTextAreaElement;
    if (!ta || ta.tagName !== 'TEXTAREA') {
      return;
    }
    if (this.disabled || this.readOnlyWhileScanning || this.tab !== 0) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const raw = ta.value.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    if (start !== end || !this.isEnterAllowedAt(raw, start)) {
      this.playWrongKeySound();
      return;
    }

    const inserted = `${raw.slice(0, start)}\n${raw.slice(end)}`;
    this.handleSaplingGenesInput(inserted, start + 1);
  };

  private bindGeneTextareaScrollListener() {
    if (this.tab !== 0) {
      return;
    }
    const ta = this.getGeneTextareaEl();
    if (!ta || this.textareaScrollListenerBound === ta) {
      return;
    }
    this.unbindGeneTextareaScrollListener();
    ta.addEventListener('scroll', this.onGeneTextareaScroll, { passive: true });
    ta.addEventListener('keydown', this.onGeneTextareaKeydownCapture, true);
    this.textareaScrollListenerBound = ta;
    this.geneTextareaScrollTop = ta.scrollTop;
  }

  private unbindGeneTextareaScrollListener() {
    const ta = this.textareaScrollListenerBound;
    if (ta) {
      ta.removeEventListener('scroll', this.onGeneTextareaScroll);
      ta.removeEventListener('keydown', this.onGeneTextareaKeydownCapture, true);
    }
    this.textareaScrollListenerBound = null;
  }

  @Watch('tab')
  onGeneTabChange() {
    if (this.tab !== 0) {
      this.unbindGeneTextareaScrollListener();
      this.teardownGeneTextareaResizeObserver();
      this.teardownGeneEditorResizeObserver();
      this.teardownWindowResizeListener();
      this.geneTextareaScrollTop = 0;
    } else {
      this.$nextTick(() => {
        this.bindGeneTextareaScrollListener();
        this.setupGeneTextareaResizeObserver();
        this.setupGeneEditorResizeObserver();
        this.setupWindowResizeListener();
        this.refreshGeneTextareaGeometry();
      });
    }
  }

  @Watch('saplingGenes')
  onSaplingGenesLengthChange() {
    this.$nextTick(() => {
      this.syncVuetifyTextareaLazyValue();
      this.syncOverlayClipGeometry();
      this.triggerGeneTextareaAutoGrow();
    });
  }

  /** auto-grow normally runs on input; scanner / URL / saved sets update :value only. */
  private triggerGeneTextareaAutoGrow() {
    if (this.tab !== 0) {
      return;
    }
    const field = this.$refs.saplingGenesInput as (Vue & { calculateInputHeight?: () => void }) | undefined;
    field?.calculateInputHeight?.();
  }

  /** Vuetify keeps internal lazyValue; keep it aligned when :value changes without @input (saved sets, URL, scan). */
  private syncVuetifyTextareaLazyValue() {
    const field = this.$refs.saplingGenesInput as (Vue & { lazyValue?: string }) | undefined;
    if (field && typeof field.lazyValue === 'string') {
      field.lazyValue = this.saplingGenes;
    }
  }

  /**
   * After tab switch or programmatic gene text, remeasure auto-grow (textarea is display:none on Saved tab,
   * so height stays stale until we recalculate once Current is visible).
   */
  private refreshGeneTextareaGeometry() {
    if (this.tab !== 0) {
      return;
    }
    this.syncVuetifyTextareaLazyValue();
    this.triggerGeneTextareaAutoGrow();
    this.$nextTick(() => {
      this.syncOverlayClipGeometry();
      requestAnimationFrame(() => {
        this.triggerGeneTextareaAutoGrow();
        this.syncOverlayClipGeometry();
        this.setupGeneTextareaResizeObserver();
      });
    });
  }

  private syncOverlayClipGeometry() {
    const editor = this.$refs.geneEditor as HTMLElement | undefined;
    const ta = this.getGeneTextareaEl();
    if (!editor || !ta) {
      return;
    }
    const er = editor.getBoundingClientRect();
    const tr = ta.getBoundingClientRect();
    this.overlayClipTopPx = Math.max(0, Math.round(tr.top - er.top));
    /* Border box matches the visible textarea; clientHeight can diverge and leak a row. */
    this.overlayClipHeightPx = Math.max(48, Math.round(tr.height));

    /* Match line # / preview / highlights to the textarea’s real line boxes (Vuetify padding, font, etc.). */
    const cs = window.getComputedStyle(ta);
    const bt = parseFloat(cs.borderTopWidth) || 0;
    const pt = parseFloat(cs.paddingTop) || 0;
    let lh = parseFloat(cs.lineHeight);
    if (Number.isNaN(lh)) {
      const fs = parseFloat(cs.fontSize) || 16;
      lh = fs * 1.45;
    }
    editor.style.setProperty('--gene-ta-gutter-top', `${bt + pt}px`);
    editor.style.setProperty('--gene-ta-line-height', `${lh}px`);
  }

  private setupGeneTextareaResizeObserver() {
    if (this.tab !== 0 || typeof ResizeObserver === 'undefined') {
      return;
    }
    const ta = this.getGeneTextareaEl();
    if (!ta) {
      return;
    }
    this.teardownGeneTextareaResizeObserver();
    this.syncOverlayClipGeometry();
    this.geneTextareaResizeObserver = new ResizeObserver(() => {
      this.syncOverlayClipGeometry();
    });
    this.geneTextareaResizeObserver.observe(ta);
  }

  private teardownGeneTextareaResizeObserver() {
    this.geneTextareaResizeObserver?.disconnect();
    this.geneTextareaResizeObserver = null;
  }

  private setupGeneEditorResizeObserver() {
    if (this.tab !== 0 || typeof ResizeObserver === 'undefined') {
      return;
    }
    const editor = this.$refs.geneEditor as HTMLElement | undefined;
    if (!editor) {
      return;
    }
    this.teardownGeneEditorResizeObserver();
    this.geneEditorResizeObserver = new ResizeObserver(() => {
      this.syncOverlayClipGeometry();
    });
    this.geneEditorResizeObserver.observe(editor);
  }

  private teardownGeneEditorResizeObserver() {
    this.geneEditorResizeObserver?.disconnect();
    this.geneEditorResizeObserver = null;
  }

  private setupWindowResizeListener() {
    if (this.windowResizeBound) {
      return;
    }
    this.windowResizeBound = () => {
      this.syncOverlayClipGeometry();
    };
    window.addEventListener('resize', this.windowResizeBound, { passive: true });
  }

  private teardownWindowResizeListener() {
    if (this.windowResizeBound) {
      window.removeEventListener('resize', this.windowResizeBound);
      this.windowResizeBound = null;
    }
  }

  /**
   * @param caretHint — caret index in `value` when the textarea hasn’t been updated yet (e.g. synthetic newline).
   */
  handleSaplingGenesInput(value: string, caretHint?: number) {
    this.prepareCheckForDatedInputActivity();
    const textarea = (this.$refs.saplingGenesInput as Vue).$el.querySelector('textarea') as HTMLTextAreaElement | null;
    if (!textarea) {
      return;
    }

    const caretBefore = caretHint !== undefined ? caretHint : textarea.selectionStart;
    const selEnd = caretHint !== undefined ? caretHint : textarea.selectionEnd;
    const collapsedSelection = selEnd === caretBefore;

    const normIncoming = value.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const hadStructuralNewlineIssue =
      this.rawGeneInputHadMultipleNewlines(normIncoming) || this.geneTextHasMiddleGapLine(normIncoming);

    this.saplingGenes = value;
    this.onNextTickRerender(() => {
      let next = this.sanitizeGeneInputValue(value);
      let caretSan = this.caretIndexAfterSanitize(value, caretBefore);

      if (!this.disabled && !this.readOnlyWhileScanning) {
        const beforeBreak = next;
        next = this.breakLongGeneLines(next);
        if (collapsedSelection) {
          caretSan += next.length - beforeBreak.length;
          const bumped = this.maybeInsertNewlineAfterSixGenes(next, caretSan);
          next = bumped.text;
          caretSan = bumped.caret;
        } else {
          caretSan = next.length;
        }
      }

      next = next.replace(/\n{2,}/g, '\n');

      if (hadStructuralNewlineIssue || this.geneTextHasMiddleGapLine(next)) {
        caretSan = this.getPreferredGeneCaretOffset(next);
      }

      this.saplingGenes = next;
      this.checkFormValidity();
      this.onNextTickRerender(() => {
        const clamped = Math.max(0, Math.min(caretSan, next.length));
        textarea.selectionStart = clamped;
        textarea.selectionEnd = clamped;
      });
    });
  }

  private sanitizeGeneInputValue(v: string): string {
    let s = v.toUpperCase().replace(/[^GHWYX\n]/g, '');
    if (s.length !== 0 && s.charAt(0).match(/\r?\n/)) {
      s = s.slice(1);
    }
    /* No empty “rows” between plants: A\n\nB becomes A\nB (fixes gaps, paste, slipped Enter). */
    s = s.replace(/\n{2,}/g, '\n');
    return s;
  }

  /** Map a caret index in the raw textarea value to the index in `sanitizeGeneInputValue(raw)`. */
  private caretIndexAfterSanitize(raw: string, caret: number): number {
    return this.sanitizeGeneInputValue(raw.slice(0, Math.min(caret, raw.length))).length;
  }

  private rawGeneInputHadMultipleNewlines(raw: string): boolean {
    return /\n{2,}/.test(raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n'));
  }

  /** True when split lines show an empty row with a non-empty row below (should not survive after sanitize). */
  private geneTextHasMiddleGapLine(norm: string): boolean {
    const lines = norm.split('\n');
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].length === 0 && lines.slice(i + 1).some((ln) => ln.length > 0)) {
        return true;
      }
    }
    return false;
  }

  /** Split runs of more than six G/H/Y/W/X on one line (e.g. paste) into separate plants. */
  private breakLongGeneLines(s: string): string {
    return s
      .split('\n')
      .map((line) => {
        if (!/^[GHWYX]+$/.test(line) || line.length <= 6) {
          return line;
        }
        const chunks: string[] = [];
        for (let i = 0; i < line.length; i += 6) {
          chunks.push(line.slice(i, i + 6));
        }
        return chunks.join('\n');
      })
      .join('\n');
  }

  private maybeInsertNewlineAfterSixGenes(sanitized: string, caretSan: number): { text: string; caret: number } {
    if (caretSan < 6) {
      return { text: sanitized, caret: caretSan };
    }

    const lastNl = sanitized.lastIndexOf('\n', caretSan - 1);
    const lineStart = lastNl + 1;
    const nextNl = sanitized.indexOf('\n', lineStart);
    const lineEnd = nextNl === -1 ? sanitized.length : nextNl;

    if (caretSan !== lineStart + 6 || lineEnd !== lineStart + 6) {
      return { text: sanitized, caret: caretSan };
    }

    const line = sanitized.slice(lineStart, lineEnd);
    if (!/^[GHWYX]{6}$/.test(line)) {
      return { text: sanitized, caret: caretSan };
    }

    const text = `${sanitized.slice(0, caretSan)}\n${sanitized.slice(caretSan)}`;
    return { text, caret: caretSan + 1 };
  }

  prepareCheckForDatedInputActivity() {
    this.wasLastInputLongTimeAgo = false;
    clearTimeout(this.lastInputLongAgoTimeoutRef);
    this.lastInputLongAgoTimeoutRef = setTimeout(() => {
      this.wasLastInputLongTimeAgo = true;
    }, 2000);
  }

  handleSaplingGenesInputFocus() {
    this.isInputFocused = true;
    this.prepareCheckForDatedInputActivity();
    this.$nextTick(() => this.moveGeneCaretToPreferred());
  }

  handleSaplingGenesTextareaClick() {
    if (this.disabled || this.readOnlyWhileScanning) {
      return;
    }
    this.$nextTick(() => this.moveGeneCaretToPreferred());
  }

  handleSaplingGenesInputBlur() {
    this.isInputFocused = false;
    this.saplingGenes = this.saplingGenes.replaceAll(/[\n]{2,}/g, '\n');
    this.saplingGenes = this.getDeduplicatedSaplingGeneList().join('\n');
    this.checkFormValidity();
  }

  handleStoreSetClick() {
    this.storeCurrentSet();
  }

  checkFormValidity() {
    this.onNextTickRerender(() => {
      if ((this.$refs.form as Vue & { validate: () => boolean }).validate()) {
        (this.$refs.form as Vue & { resetValidation: () => boolean }).resetValidation();
      }
      this.$emit('validity-change', this.isFormValid);
    });
  }

  /** Whole-line match only (avoids `indexOf` substring false positives). */
  saplingLineAlreadyPresent(line: string): boolean {
    return this.saplingGeneList.some((row) => {
      const normalized = row
        .trim()
        .toUpperCase()
        .replace(/[^GHWYX]/g, '');
      return normalized === line;
    });
  }

  handleSaplingScannedEvent(value: string | undefined) {
    if (value == null || typeof value !== 'string') {
      return;
    }
    const line = value
      .trim()
      .toUpperCase()
      .replace(/[^GHWYX]/g, '');
    if (!/^[GHWYX]{6}$/.test(line)) {
      return;
    }
    if (!this.saplingLineAlreadyPresent(line)) {
      let next = this.saplingGenes;
      const last = next.length > 0 ? next.charAt(next.length - 1) : '';
      if (!last.match(/\r?\n/) && next.length !== 0) {
        next += '\n';
      }
      next += line;
      this.saplingGenes = next;
      // Vuetify 2 sometimes keeps an internal lazyValue out of sync with :value when readonly; force match.
      this.onNextTickRerender(() => {
        this.syncVuetifyTextareaLazyValue();
        this.animateAndScrollToLastSapling();
        this.playSaplingsScannedSound();
        this.checkFormValidity();
      });
    }
  }

  handlePreviousGenesSelectedEvent(set: StoredSet) {
    this.saplingGenes = set.genes;
    eventBus.$emit(GLOBAL_EVENT_SELECTED_PLANT_TYPE_CHANGED, set.selectedPlantTypeName);
    this.tab = 0;
    this.checkFormValidity();
    this.$nextTick(() => {
      this.refreshGeneTextareaGeometry();
      this.$nextTick(() => {
        this.focusTextArea();
      });
    });
  }

  focusTextArea() {
    this.onNextTickRerender(() => {
      const textArea = this.getGeneTextareaEl();
      if (!textArea) {
        return;
      }
      textArea.focus();
      this.moveGeneCaretToPreferred(textArea);
    });
  }

  storeCurrentSet() {
    const previousGenesComponent = this.$refs.previousGenes as PreviousGenes;
    if (previousGenesComponent) {
      const wasAdded = previousGenesComponent.addNewSet(this.saplingGenes);
      if (wasAdded) {
        this.animatePreviousGenesTab();
      }
    }
  }

  animatePreviousGenesTab() {
    this.animatePreviousGenesTabIn = true;
    setTimeout(() => {
      this.animatePreviousGenesTabIn = false;
    }, 700);
  }

  getDeduplicatedSaplingGeneList() {
    const splitSaplingGenes: string[] = this.saplingGeneList;
    const deduplicatedSaplingGenes: string[] = splitSaplingGenes.filter(
      (genes, index, self) => index === self.findIndex((otherGenes) => otherGenes === genes)
    );
    return deduplicatedSaplingGenes;
  }

  handleSaplingGenesInputKeyDown(event: KeyboardEvent) {
    /* Enter is handled in capture phase on the textarea (onGeneTextareaKeydownCapture). */

    const allowedKeys = [
      'H',
      'G',
      'Y',
      'X',
      'W',
      'Enter',
      'Backspace',
      'ArrowRight',
      'ArrowLeft',
      'ArrowUp',
      'ArrowDown',
      'End',
      'Delete',
      'Home',
      'Shift',
      'Alt',
      'Control',
      'Tab'
    ];
    if (
      event.key &&
      event.key !== 'Unidentified' &&
      !(event.altKey || event.ctrlKey) &&
      allowedKeys.indexOf(event.key) === -1 &&
      allowedKeys.indexOf(event.key.toUpperCase()) === -1
    ) {
      this.playWrongKeySound();
    }
  }

  animateAndScrollToLastSapling() {
    (this.$refs.saplingListPreview as SaplingListPreview)?.animateAndScrollToLastSapling();
  }

  playSaplingsScannedSound() {
    if (this.soundsEnabled) {
      playAudio('saplingScannedAudio', 0.5);
    }
  }

  playWrongKeySound() {
    if (this.soundsEnabled) {
      playAudio('wrongKeyAudio', 0.04);
    }
  }
}
